import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HomeSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 0 2rem 0 0;
  position: relative;
  overflow: hidden;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 0 0 4rem 0;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 0 0 4rem 0;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    padding: 0 10rem 0 0;
  }
`;

const Container = styled.div`
  max-width: 1350px;
  margin: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 7rem;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: start;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    max-width: none;
    height: 100vh;
  }
`;

const Content = styled.div`
  z-index: 1;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const Greeting = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.colors.secondary};
  line-height: normal;

  span {
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: ${(props) => props.theme.colors.gray.dark};
  max-width: 600px;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    margin: 0 3rem 2rem;
  }
`;

const SkillsContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    justify-content: center;
    width: 80%;
    margin: auto;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    width: 80%;
    margin: auto;
  }
`;

const Skill = styled(motion.div)`
  background: ${(props) => props.theme.colors.white};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  color: ${(props) => props.theme.colors.secondary};
  font-weight: 500;
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  justify-self: start;

  img {
    @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
      width: 100vw;
      max-width: none;
    }
    @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
      height: 100vh;
      width: auto;
      max-width: none;
    }
    @media (min-width: ${(props) =>
        props.theme.breakpoints.tablet} and max-width: ${(props) =>
        props.theme.breakpoints.desktop}) {
      width: 100%;
    }
  }
`;

const skills = [
  'React.js',
  'Vue.js',
  'Thymeleaf',
  'JS/HTML/CSS',
  'UI/UX',
  'Watercolor',
  'Illustration',
  'Visual Storytelling',
  'Existential Acrobatics',
];

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
  },
};

const Home: React.FC = () => {
  return (
    <HomeSection id='home'>
      <Container>
        <ImageContainer
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <img src={`./img/me-2.jpg`} alt='' />
        </ImageContainer>
        <Content>
          <motion.div
            variants={containerVariants}
            initial='hidden'
            animate='visible'
          >
            <Greeting variants={itemVariants}>
              Hi! I'm <span>Zara Zamanian</span>
            </Greeting>
            <Description variants={itemVariants}>
              A <strong>Front-End Developer</strong> by daylight, rogue
              <strong> Illustrator</strong> by moonlight — and once an architect
              and a city enthusiast.
              <br /> This site is part portfolio, part playground, part
              existential circus. Mostly, it’s a lovingly over-engineered
              attempt to impress you with complicated codes and animations no
              one asked for. Please be impressed — or, maybe at least mildly
              amused?
            </Description>
            <SkillsContainer variants={containerVariants}>
              {skills.map((skill, index) => (
                <Skill key={index} variants={itemVariants}>
                  {skill}
                </Skill>
              ))}
            </SkillsContainer>
          </motion.div>
        </Content>
      </Container>
    </HomeSection>
  );
};

export default Home;
